import { useLayoutEffect, useRef, useState, type DragEvent } from "react";
import { useScaffold } from "./Scaffold";
import styles from "./Scaffold.module.css";
import {
  HORIZONTAL_SIDE_ZONES,
  IScaffoldZoneColorResolverResult,
  SCAFFOLD_ZONE_GRID_BOUNDS,
  ScaffoldDropZone,
  SIDE_ZONE_MIN_SIZE,
  SIDE_ZONE_SIZE,
  VERTICAL_SIDE_ZONES,
  ZONE_GRID_ORDER,
} from "./interface";

export interface ScaffoldZoneGridProps {
  /** Identifier of the row this zone grid belongs to. */
  rowId: string;
  /** Identifier of the column this zone grid belongs to. */
  colId: string;
}

/**
 * Renders a drop-zone overlay grid with drag event handling.
 * Non-centre zones are {@link SIDE_ZONE_SIZE}px wide/tall. Side zones along a given axis are hidden
 * when the container is smaller than {@link SIDE_ZONE_MIN_SIZE}px on that axis.
 * The centre area is represented by four quadrant zones, bisecting the centre on both axes.
 * The container dimensions are measured from the component's own div via a ResizeObserver.
 * The outer div is always rendered so that dimensions are available before the first drag.
 *
 * @param props - Component props
 * @param props.rowId - Row identifier passed to the onDrop callback
 * @param props.colId - Column identifier passed to the onDrop callback
 * @returns A React element representing the zone grid overlay.
 */
export const ScaffoldZoneGrid = ({ rowId, colId }: ScaffoldZoneGridProps) => {
  const scaffoldContext = useScaffold();
  const [activeZone, setActiveZone] = useState<ScaffoldDropZone | null>(null);
  const [activeHighlightedZones, setActiveHighlightedZones] = useState<Set<ScaffoldDropZone>>(
    new Set(),
  );
  const [activeHighlightColor, setActiveHighlightColor] = useState<string | undefined>(undefined);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const divRef = useRef<HTMLDivElement>(null);

  /**
   * Resolves highlight behaviour for a zone from static or function-based zoneColors.
   *
   * @param zone - Zone being evaluated.
   * @returns Resolved highlight colour and additional highlighted zones.
   */
  const resolveZoneHighlight = (zone: ScaffoldDropZone): IScaffoldZoneColorResolverResult => {
    const zoneColors = scaffoldContext.settings.zoneColors;
    const row =
      scaffoldContext.settings.rows.find((r) => r.id === rowId) ??
      scaffoldContext.state.leavingRows.find((r) => r.id === rowId);
    const col =
      scaffoldContext.settings.cols.find((c) => c.id === colId) ??
      scaffoldContext.state.leavingCols.find((c) => c.id === colId);

    if (typeof zoneColors === "function") {
      return zoneColors({
        rowId,
        colId,
        rowNumber: row?.rowNumber,
        colNumber: col?.colNumber,
        zone,
      });
    }

    return {
      color: zoneColors[zone],
      additionalHighlightedZones: [],
    };
  };

  useLayoutEffect(() => {
    const el = divRef.current;
    /* istanbul ignore else */
    if (el) {
      const observer = new ResizeObserver(([entry]) => {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      });
      observer.observe(el);
      return () => observer.disconnect();
    }
  }, []);

  /** Hide zones if the container is smaller than the minimum size for side zones */
  const showHorizontalSides = containerSize.width >= SIDE_ZONE_MIN_SIZE;
  const showVerticalSides = containerSize.height >= SIDE_ZONE_MIN_SIZE;

  const visibleZones = ZONE_GRID_ORDER.filter((zone) => {
    if (!showHorizontalSides && HORIZONTAL_SIDE_ZONES.has(zone)) return false;
    if (!showVerticalSides && VERTICAL_SIDE_ZONES.has(zone)) return false;
    return true;
  });

  const colTemplate = showHorizontalSides
    ? `${SIDE_ZONE_SIZE}px 1fr 1fr ${SIDE_ZONE_SIZE}px`
    : "1fr 1fr";
  const rowTemplate = showVerticalSides
    ? `${SIDE_ZONE_SIZE}px 1fr 1fr ${SIDE_ZONE_SIZE}px`
    : "1fr 1fr";

  /**
   * Clears any active zone highlight state.
   */
  const clearActiveZoneHighlight = () => {
    setActiveZone(null);
    setActiveHighlightedZones(new Set());
    setActiveHighlightColor(undefined);
  };

  /**
   * Handles drag-over highlighting for a zone.
   *
   * @param event - Drag event from the hovered zone element.
   * @param zone - Zone currently being hovered.
   */
  const onZoneDragOver = (event: DragEvent<HTMLDivElement>, zone: ScaffoldDropZone) => {
    const highlight = resolveZoneHighlight(zone);
    if (highlight.color !== null) {
      event.preventDefault();

      const highlightedZones = new Set<ScaffoldDropZone>([
        zone,
        ...(highlight.additionalHighlightedZones ?? []),
      ]);
      setActiveZone(zone);
      setActiveHighlightedZones(highlightedZones);
      setActiveHighlightColor(highlight.color);
    } else {
      clearActiveZoneHighlight();
    }
  };

  /**
   * Clears highlight state when the pointer leaves the zone-grid container.
   *
   * @param event - Drag leave event from any zone cell.
   */
  const onZoneDragLeave = (event: DragEvent<HTMLDivElement>) => {
    // Only clear zone if leaving entirely (not moving to a sibling zone in the grid)
    if (
      event.relatedTarget instanceof Node &&
      event.currentTarget.parentElement?.contains(event.relatedTarget)
    ) {
      return;
    }
    clearActiveZoneHighlight();
  };

  /**
   * Handles a drop for a zone, including optional drop suppression via zone-color resolver.
   *
   * @param event - Drop event from the target zone element.
   * @param zone - Zone receiving the drop.
   */
  const onZoneDrop = (event: DragEvent<HTMLDivElement>, zone: ScaffoldDropZone) => {
    const highlight = resolveZoneHighlight(zone);
    if (highlight.color !== null) {
      event.preventDefault();
      event.stopPropagation();
      clearActiveZoneHighlight();
      scaffoldContext.settings.onDrop?.(event, rowId, colId, zone);
    } else {
      clearActiveZoneHighlight();
    }
  };

  return (
    <div
      ref={divRef}
      className={styles.zoneGrid}
      style={{
        gridTemplateColumns: colTemplate,
        gridTemplateRows: rowTemplate,
        zIndex:
          scaffoldContext.state.isDragging && !scaffoldContext.settings.disabled
            ? 1
            : // istanbul ignore next
              -1,
      }}
    >
      {scaffoldContext.state.isDragging &&
        visibleZones.map((zone) => {
          const canonicalBounds = SCAFFOLD_ZONE_GRID_BOUNDS[zone];
          const rowOffset = showVerticalSides ? 0 : 1;
          const colOffset = showHorizontalSides ? 0 : 1;
          const top = canonicalBounds.top - rowOffset;
          const bottom = canonicalBounds.bottom - rowOffset;
          const left = canonicalBounds.left - colOffset;
          const right = canonicalBounds.right - colOffset;
          return (
            <div
              key={zone}
              data-zone={zone}
              className={styles.zoneGridCell}
              onDragOver={(event) => onZoneDragOver(event, zone)}
              onDragLeave={onZoneDragLeave}
              onDrop={(event) => onZoneDrop(event, zone)}
              style={{
                gridRow: `${top} / ${bottom}`,
                gridColumn: `${left} / ${right}`,
                backgroundColor:
                  activeZone !== null &&
                  activeHighlightedZones.has(zone) &&
                  activeHighlightColor !== undefined
                    ? activeHighlightColor
                    : "transparent",
                opacity:
                  activeZone !== null &&
                  activeHighlightedZones.has(zone) &&
                  activeHighlightColor !== undefined
                    ? 0.5
                    : 0,
                border:
                  activeZone !== null &&
                  activeHighlightedZones.has(zone) &&
                  activeHighlightColor !== undefined
                    ? `2px solid ${activeHighlightColor}`
                    : "none",
              }}
            />
          );
        })}
    </div>
  );
};

ScaffoldZoneGrid.displayName = "ScaffoldZoneGrid";
